import { useState, useRef, useEffect } from "react";
import { useChat } from "hooks/useChat";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { MessageBalloon } from "./MessageBalloon";
import Message from "types/Message";

import React from "react";
import { connectToWebSocket, toCamelCase, uploadFile } from "apiClient";
import { useAlerts } from "context/AlertsContext";
import Student from "types/Student";
import { useAuth } from "context/AuthContext";
import { Paperclip, X } from "lucide-react";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";

interface ChatProps {
  teacherId: number;
  student: Student;
  phone: string;
}

export function Chat({ teacherId, student, phone }: ChatProps) {
  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const { markAlertsAsRead } = useAlerts();

  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);

  const { teacherName } = useAuth();

  const previousStudentIdRef = useRef<number | null>(null);
  const loadedPages = useRef<Set<number>>(new Set());
  const socketRef = useRef<ReturnType<typeof connectToWebSocket> | null>(null);
  const socketStudentIdRef = useRef<number | null>(null);
  const previousScrollHeight = useRef<number>(0);
  const pageRef = useRef(1);
  const nextRef = useRef<string | null>(null);
  const loadingRef = useRef(false);

  const studentId = student.id;

  // Reset page to 1 when studentId changes
  useEffect(() => {
    if (previousStudentIdRef.current !== studentId) {
      previousStudentIdRef.current = studentId;
      setPage(1);
      pageRef.current = 1;
    }
  }, [studentId]);

  const { messages, loading, error, next, sendMessage } = useChat(
    teacherId,
    studentId,
    page,
  );

  // Keep refs up to date
  useEffect(() => {
    pageRef.current = page;
    nextRef.current = next;
    loadingRef.current = loading;
  }, [page, next, loading]);

  // Use infinite scroll hook for loading older messages
  const loadMoreMessages = () => {
    if (!loadingRef.current && nextRef.current) {
      const nextPage = pageRef.current + 1;
      if (!loadedPages.current.has(nextPage)) {
        setPage(nextPage);
      }
    }
  };

  const scrollContainerRef = useInfiniteScroll({
    hasMore: !!next,
    loading,
    onLoadMore: loadMoreMessages,
  });

  useEffect(() => {
    // Close any existing socket before creating a new one
    if (socketRef.current) {
      socketRef.current.close();
      socketStudentIdRef.current = null;
    }

    socketStudentIdRef.current = studentId;

    const socket = connectToWebSocket(`/ws/chat/${phone}/`, (event) => {
      // Only process messages if this socket is still for the current contact
      if (socketStudentIdRef.current !== studentId) {
        return;
      }

      const message = toCamelCase(JSON.parse(event.data));
      setAllMessages((prev) => {
        // Remove optimistic messages with same content
        const filtered = prev.filter(
          (m) => !(m.id < 0 && m.content === message.content),
        );

        // Check if this message already exists (prevent duplicates)
        if (filtered.some((m) => m.id === message.id)) {
          return prev;
        }

        return [message, ...filtered].sort(
          (a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt),
        );
      });
    });

    socketRef.current = socket;

    return () => {
      socket.close();
      socketRef.current = null;
      socketStudentIdRef.current = null;
    };
  }, [phone, studentId]);

  useEffect(() => {
    // Reset everything when contactId or messageId changes
    setAllMessages([]);
    setPage(1);
    loadedPages.current = new Set();
    previousScrollHeight.current = 0;
    pageRef.current = 1;
    nextRef.current = null;
    loadingRef.current = false;
    previousStudentIdRef.current = studentId;
  }, [teacherId, studentId]);

  useEffect(() => {
    // Process messages if:
    // 1. We haven't loaded this page yet, OR
    // 2. We have messages but allMessages is empty (race condition fix)
    const shouldProcess =
      !loadedPages.current.has(page) ||
      (messages.length > 0 && allMessages.length === 0 && page === 1);

    if (shouldProcess) {
      if (messages && messages.length > 0) {
        // Store scroll height and position before adding new messages (for scroll position restoration)
        const container = scrollContainerRef.current;
        let scrollHeightBefore = 0;
        let scrollTopBefore = 0;

        if (page > 1 && container) {
          scrollHeightBefore = container.scrollHeight;
          scrollTopBefore = container.scrollTop;
          previousScrollHeight.current = scrollHeightBefore;
        }

        setAllMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMessages = messages.filter((m) => !existingIds.has(m.id));

          if (newMessages.length > 0) {
            loadedPages.current.add(page);
          }

          return [...prev, ...newMessages];
        });

        // Restore scroll position after DOM updates
        if (page > 1 && container && scrollHeightBefore > 0) {
          // Use requestAnimationFrame to wait for DOM to update
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const heightDiff = newScrollHeight - scrollHeightBefore;
                if (heightDiff > 0) {
                  // Adjust scroll position to maintain visual position
                  container.scrollTop = scrollTopBefore + heightDiff;
                }
                previousScrollHeight.current = 0;
              }
            });
          });
        }
      } else if (!loading && messages && messages.length === 0) {
        // Only mark as loaded if loading is complete AND we have an empty array
        // This prevents marking as loaded while still loading
        loadedPages.current.add(page);
      }
    }
  }, [messages, page, loading, allMessages.length, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleBottom = () => {
      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        50;

      if (isAtBottom) {
        markAlertsAsRead(studentId);
      }
    };

    container.addEventListener("scroll", handleBottom);
    return () => container.removeEventListener("scroll", handleBottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, markAlertsAsRead]);

  // Scroll to bottom on first load only (if no message-id)
  useEffect(() => {
    if (page === 1 && allMessages.length > 0 && scrollContainerRef.current) {
      // Scroll to bottom (which is the end of the reversed list)
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, allMessages]);

  const [messageToSend, setMessageToSend] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError("El archivo es demasiado grande (máx 10MB)");
        setSelectedFile(null);
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
    // Reset input value so same file can be selected again if removed
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const handleSend = async () => {
    const content = messageToSend.trim();
    if (!content && !selectedFile) return;

    let fileUrl: string | null = null;
    if (selectedFile) {
      setUploading(true);
      try {
        fileUrl = await uploadFile(selectedFile);
      } catch (err) {
        setFileError("Error subiendo el archivo");
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const optimisticMessage: Message = {
      id: -Date.now(),
      type: "teacher",
      content,
      fileUrl,
      sentAt: new Date().toISOString(),
    };

    setAllMessages((prev) => [optimisticMessage, ...prev]);
    sendMessage(content, fileUrl);
    setMessageToSend("");
    setSelectedFile(null);
    setFileError(null);
  };

  const initialMessage: Message = {
    id: -1,
    type: "teacher-response",
    content:
      lang === "es"
        ? `Hola ${teacherName}, soy Eduvia.

Estoy listo para ayudar a ${student.name}. Si querés que me presente con él/ella, avisame y le escribo.

En este chat vas a ver también todo lo que ${student.name} me escribe.

Podés mandarme por acá cualquier indicación o pregunta sobre cómo acompañarlo/a — por ejemplo, pedir que le ponga un ejercicio, que le recuerde agendar una clase, o que le envíe un mensaje de seguimiento.`
        : `Hi ${teacherName}, I'm Eduvia.

I'm ready to assist ${student.name}. If you want me to introduce myself to him/her, just let me know and I'll send a message.

In this chat, you'll also see everything that ${student.name} writes to me.

Feel free to send me any instructions or questions about how to support him/her — for example, asking me to assign an exercise, remind them to schedule a class, or send a follow-up message.`,

    fileUrl: null,
    sentAt: student.createdAt,
  };

  return (
    <Card
      className="chat-container flex flex-col h-full p-2 lg:p-4 space-y-2 lg:space-y-4"
      style={{ backgroundColor: "#FEF4E8" }}
    >
      {/* Messages list */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto space-y-2 lg:space-y-4 pr-1 w-full"
      >
        {error && (
          <div className="text-red-500 text-xs lg:text-sm">{error}</div>
        )}

        {allMessages.length > 0 ? (
          [...allMessages]
            .reverse()
            .map((msg) => <MessageBalloon key={msg.id} message={msg} />)
        ) : (
          <MessageBalloon key={student.id} message={initialMessage} />
        )}
      </div>

      {/* Message input */}
      <div className="flex items-center gap-1 lg:gap-2">
        <textarea
          value={messageToSend}
          onChange={(e) => setMessageToSend(e.target.value)}
          placeholder="Send instruction to Eduvia..."
          className="flex-1 border border-gray-300 rounded p-2 text-xs lg:text-sm resize-none h-10 lg:h-12"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (e.metaKey || e.shiftKey) {
                return;
              }
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={uploading}
        />
        <label className="cursor-pointer flex items-center p-1 lg:p-2">
          <input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <Paperclip className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500" />
        </label>
        <Button
          variant="primary"
          size="sm"
          className="h-10 lg:h-12 px-2 lg:px-3 mr-12 lg:mr-16"
          onClick={handleSend}
          disabled={uploading || !messageToSend.trim()}
        >
          {uploading ? tr("Enviando...", "Sending...") : tr("Send", "Send")}
        </Button>
      </div>
      {selectedFile && (
        <div className="flex items-center mt-1 bg-gray-100 rounded px-2 py-1 text-xs">
          <span className="truncate max-w-xs">{selectedFile.name}</span>
          <button onClick={handleRemoveFile} className="ml-2">
            <X className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
          </button>
        </div>
      )}
      {fileError && (
        <div className="text-xs text-red-500 mt-1">{fileError}</div>
      )}
    </Card>
  );
}
